import { Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import { formatUtc } from '@/lib/format-utc'
import type { MappingDraftDTO } from '@/services/workfront-mappings.service'

export interface DraftBarProps {
	draft: MappingDraftDTO
	/** The name of the user who opened the draft. */
	openedByName: string
	/** A command is in flight: the actions wait for it. */
	busy?: boolean
	onPreview: () => void
	onPublish: () => void
	onDiscard: () => void
}

const plural = (count: number, one: string, many: string) =>
	`${count.toLocaleString('en-US')} ${count === 1 ? one : many}`

/**
 * `3 field changes · 12 values · 1 asset type`: only the kinds the draft touches. A version
 * published before PM rules were counted apart has no `pmRules`, and counts them as lookups.
 */
export function draftCountsLine({ fields, values, assetTypes, lookups, pmRules }: MappingDraftDTO['counts']): string {
	const parts = [
		fields ? plural(fields, 'field change', 'field changes') : null,
		values ? plural(values, 'value', 'values') : null,
		assetTypes ? plural(assetTypes, 'asset type', 'asset types') : null,
		lookups ? plural(lookups, 'lookup row', 'lookup rows') : null,
		pmRules ? plural(pmRules, 'PM rule', 'PM rules') : null,
	].filter(Boolean)
	return parts.length ? parts.join(' · ') : 'no changes yet'
}

/**
 * The open draft, on every Mappings tab: who opened it, what it changes, and
 * Preview, Publish and Discard. Discarding asks once more, in place.
 */
export function DraftBar({ draft, openedByName, busy, onPreview, onPublish, onDiscard }: DraftBarProps) {
	const [confirming, setConfirming] = useState(false)
	return (
		<section
			aria-label="Draft"
			className="flex flex-wrap items-center justify-between gap-3 rounded-ds-md border border-ds-border bg-ds-surface px-4 py-3"
		>
			<p className="text-ds-sm">
				<span className="font-medium">Draft by {openedByName}</span>
				<span className="text-ds-text-muted">
					{' '}
					· opened {formatUtc(draft.openedAt)} · {draftCountsLine(draft.counts)}
				</span>
			</p>
			<div className="flex flex-wrap gap-2">
				<Button size="sm" variant="outline" isDisabled={busy} onPress={onPreview}>
					Preview
				</Button>
				<Button size="sm" isDisabled={busy} onPress={onPublish}>
					Publish
				</Button>
				{confirming ? (
					<>
						<Button
							size="sm"
							variant="ghost"
							isDisabled={busy}
							onPress={() => {
								setConfirming(false)
								onDiscard()
							}}
						>
							Discard every change
						</Button>
						<Button size="sm" variant="ghost" onPress={() => setConfirming(false)}>
							Keep the draft
						</Button>
					</>
				) : (
					<Button size="sm" variant="ghost" isDisabled={busy} onPress={() => setConfirming(true)}>
						Discard
					</Button>
				)}
			</div>
		</section>
	)
}
